import { useContext, useMemo, useState } from "react";
import { AppContext } from "../App";
import { IoArrowForwardCircleOutline, IoChevronBack, IoChevronForward } from "react-icons/io5";

export default function ServiceDetailPage({
  label = "Service",
  title,
  subtitle,
  overview = "",
  images = [],
  highlights = [],
  metrics = [],
}) {
  const appContext = useContext(AppContext);
  const [slideIndex, setSlideIndex] = useState(0);

  const media = useMemo(() => {
    if (images.length > 0) return images;
    return [];
  }, [images]);

  function goPrev() {
    if (media.length < 2) return;
    setSlideIndex((prev) => (prev - 1 + media.length) % media.length);
  }

  function goNext() {
    if (media.length < 2) return;
    setSlideIndex((prev) => (prev + 1) % media.length);
  }

  return (
    <section className="MainBodySection" style={{ background: "#f5f8fc", paddingTop: "var(--topBarHeight)" }}>
      <div className="container py-5">
        <div
          className="row g-4 align-items-stretch"
          style={{
            borderRadius: 20,
            overflow: "hidden",
            background: "#fff",
            boxShadow: "0 24px 58px rgba(8,24,42,.12)",
            border: "1px solid #e3e9f0",
          }}
        >
          <div className="col-lg-5 p-4 p-lg-5" data-aos="fade-right">
            <div className="section-heading-pro mb-3">
              <span className="section-heading-pro__rail" />
              <h1 className="section-heading-pro__title font-bold" style={{ color: "#142436", lineHeight: 1.2 }}>
                {title}
              </h1>
              <p className="section-heading-pro__meta mb-0">{label}</p>
            </div>
            <p className="mb-3" style={{ color: "#4f5f72", lineHeight: 1.7 }}>
              {subtitle}
            </p>
            <p className="mb-0" style={{ color: "#5c6a79", lineHeight: 1.75 }}>
              {overview}
            </p>

            <button
              className="mt-4"
              onClick={() => appContext.navTo({ item: "contact_us" })}
              style={{
                border: "none",
                borderRadius: 10,
                background: "#102030",
                color: "#fff",
                padding: "11px 16px",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontWeight: 600,
              }}
            >
              Contact Our Team <IoArrowForwardCircleOutline size={20} />
            </button>
          </div>

          <div className="col-lg-7 p-0" data-aos="fade-up">
            <div style={{ position: "relative", height: "100%", minHeight: 430 }}>
              {media.map((item, index) => (
                <article
                  key={item.src + index}
                  style={{
                    position: "absolute",
                    inset: 0,
                    opacity: index === slideIndex ? 1 : 0,
                    transition: "opacity .6s ease",
                  }}
                >
                  <img
                    src={item.src}
                    alt={item.alt || title}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(180deg, rgba(8,21,35,.15), rgba(8,21,35,.82))",
                    }}
                  />
                  <div style={{ position: "absolute", left: 20, right: 20, bottom: 20, color: "#fff" }}>
                    <h3 className="font-semi-bold mb-1" style={{ fontSize: 21 }}>
                      {item.captionTitle || title}
                    </h3>
                    <p className="mb-0" style={{ lineHeight: 1.6, color: "#dbe6f1" }}>
                      {item.captionText || subtitle}
                    </p>
                  </div>
                </article>
              ))}

              {media.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={goPrev}
                    aria-label="Previous slide"
                    style={{
                      position: "absolute",
                      top: 16,
                      right: 62,
                      width: 38,
                      height: 38,
                      border: "none",
                      borderRadius: 11,
                      background: "rgba(255,255,255,.92)",
                    }}
                  >
                    <IoChevronBack size={19} />
                  </button>
                  <button
                    type="button"
                    onClick={goNext}
                    aria-label="Next slide"
                    style={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                      width: 38,
                      height: 38,
                      border: "none",
                      borderRadius: 11,
                      background: "rgba(255,255,255,.92)",
                    }}
                  >
                    <IoChevronForward size={19} />
                  </button>

                  <div style={{ position: "absolute", left: 16, top: 16, display: "flex", gap: 8 }}>
                    {media.map((item, i) => (
                      <button
                        key={item.src + "dot"}
                        onClick={() => setSlideIndex(i)}
                        aria-label={`Go to slide ${i + 1}`}
                        style={{
                          width: i === slideIndex ? 24 : 9,
                          height: 9,
                          borderRadius: 99,
                          border: "none",
                          background: i === slideIndex ? "#f9c96c" : "rgba(255,255,255,.65)",
                        }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="row g-4 mt-1">
          <div className="col-lg-7">
            <article className="modern-about-panel" style={{ height: "100%" }}>
              <h2 className="font-semi-bold mb-4" style={{ color: "#142436", fontSize: 24 }}>
                Scope & Deliverables
              </h2>
              <div className="d-grid gap-3">
                {highlights.map((item, idx) => (
                  <div
                    key={item}
                    className="modern-cap-item"
                    style={{
                      background: "linear-gradient(135deg, rgba(209, 161, 87, 0.08) 0%, rgba(209, 161, 87, 0.02) 100%)",
                      borderColor: "rgba(209, 161, 87, 0.25)",
                    }}
                  >
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#d1a157" }} />
                    <span style={{ color: "#4f5f72", lineHeight: 1.6 }}>{item}</span>
                  </div>
                ))}
              </div>
            </article>
          </div>

          <div className="col-lg-5">
            <article className="modern-about-panel" style={{ height: "100%" }}>
              <h2 className="font-semi-bold mb-4" style={{ color: "#142436", fontSize: 24 }}>
                Operational Metrics
              </h2>
              <div className="d-grid gap-3">
                {metrics.map((metric, idx) => (
                  <div
                    key={metric.label}
                    className="modern-decorated-card"
                    style={{ 
                      padding: "16px 18px",
                      "--card-accent": "#d1a157"
                    }}
                  >
                    <div className="card-accent-line" />
                    <p className="mb-1 font-bold" style={{ color: "#102030", fontSize: 20 }}>
                      {metric.value}
                    </p>
                    <p className="mb-0" style={{ color: "#59687a", fontSize: 14 }}>
                      {metric.label}
                    </p>
                  </div>
                ))}
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}
